import { DocumentsSection } from './DocumentsSection'
import { useState, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import {
  ArrowRight,
  CircleCheck,
  FileText,
  GraduationCap,
  Landmark,
  MessageSquare,
  Play,
  ShieldCheck,
  Target,
  Wrench,
  ChevronLeft,
  ChevronRight,
  Download,
  Clock,
  RotateCcw,
} from 'lucide-react'

import escuta1 from '../assets/img/escuta1.jpeg'
import escuta2 from '../assets/img/escuta2.jpeg'
import escuta3 from '../assets/img/escuta3.jpeg'

import fachadaPerspectiva from '../assets/img/01 PVH-FACHADA PERSPECTIVA.png'
import fachadaFrontal from '../assets/img/02 PVH-FACHADA FRONTAL.png'
import refeitorio from '../assets/img/03 PVH REFEITORIO.png'
import recreacao from '../assets/img/04 PHV RECREACAO.png'
import berçario from '../assets/img/05 PHV BERCARIO.png'
import fraldario from '../assets/img/06 PHV FRALDARIO.png'
import planoCortado from '../assets/img/07 PVH PLANO CORTADO.png'
import salaAtividades1 from '../assets/img/08 PVH SALA DE ATIVIDADES 1.png'
import salaAtividades2 from '../assets/img/09 PHV SALA DE ATIVIDADES 2.png'
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion'
import { WaveDivider } from '../components/WaveDivider'
import { api, Report } from '../services/api'

const faqItems = [
  {
    id: 'faq-1',
    question: 'O que é uma Parceria Público-Privada (PPP)?',
    answer: 'PPP é uma parceria contratual de longo prazo entre o poder público e uma empresa privada para entregar infraestrutura e serviços de apoio com metas de desempenho e fiscalização. As PPPs são regidas pela Lei Federal n.º 11.079/2004 e permitem atrair investimentos privados para melhorar a qualidade dos serviços públicos.',
    category: 'conceitos'
  },
  {
    id: 'faq-2',
    question: 'PPP é a mesma coisa que privatização?',
    answer: 'Não. Na PPP, os bens e serviços continuam sendo públicos. O parceiro privado é responsável pela infraestrutura e serviços de apoio por um período determinado, e ao final do contrato, tudo retorna ao Município. A gestão pedagógica permanece integralmente sob responsabilidade da Prefeitura. Já na privatização, há transferência definitiva de bens públicos ao setor privado.',
    category: 'conceitos'
  },
  {
    id: 'faq-3',
    question: 'O que entra no escopo do parceiro privado?',
    answer: 'O parceiro privado será responsável por serviços não pedagógicos, como: construção, reforma e manutenção das unidades escolares; limpeza e conservação; apoio à alimentação escolar (operacional); lavanderia e rouparia; segurança patrimonial; e fornecimento de fraldas e uniformes escolares.',
    category: 'escopo'
  },
  {
    id: 'faq-4',
    question: 'O que permanece com a Prefeitura (parte pedagógica)?',
    answer: 'Toda a gestão pedagógica permanece sob responsabilidade do poder público, incluindo: contratação e remuneração de professores; definição de currículo e práticas de ensino; transporte escolar; fornecimento de materiais didáticos; alimentação escolar (cardápio e diretrizes); e atendimento educacional especializado (AEE).',
    category: 'escopo'
  },
  {
    id: 'faq-5',
    question: 'Vai ter cobrança ou tarifa para as famílias?',
    answer: 'Não. As vagas continuam sendo públicas e gratuitas. A PPP abrange exclusivamente a infraestrutura e os serviços não pedagógicos, mantendo o ensino integralmente público. A concessionária será remunerada por contraprestações públicas, sem qualquer cobrança aos cidadãos.',
    category: 'escopo'
  },
  {
    id: 'faq-6',
    question: 'Em que fase o projeto se encontra atualmente?',
    answer: 'O projeto está na etapa final de Planejamento, Diagnóstico e Estudos (Etapa 1). Nesta fase, são realizados levantamentos de dados, estudos de viabilidade e diagnósticos para subsidiar a estruturação da concessão.',
    category: 'status'
  },
  {
    id: 'faq-7',
    question: 'Quando haverá Consulta Pública e Audiência Pública?',
    answer: 'A Consulta Pública e a Audiência Pública serão realizadas na fase de Estruturação do Contrato (Etapa 3), antes da licitação do projeto. A Prefeitura realizará divulgação prévia em seus canais oficiais.',
    category: 'status'
  },
  {
    id: 'faq-8',
    question: 'Como a população pode participar agora?',
    answer: 'A população pode participar por meio da Escuta Social, respondendo ao questionário online disponível neste hotsite.',
    category: 'participacao'
  },
  {
    id: 'faq-9',
    question: 'Como será garantida a qualidade dos serviços?',
    answer: 'O contrato de PPP prevê a adoção de mecanismos de monitoramento de desempenho e de transparência para acompanhar a qualidade dos serviços não pedagógicos.',
    category: 'governanca'
  },
  {
    id: 'faq-10',
    question: 'O que acontece com os bens ao final do contrato?',
    answer: 'Ao final do período contratual, todos os bens reversíveis — como as escolas construídas ou reformadas — retornam ao poder público municipal.',
    category: 'governanca'
  },
  {
    id: 'faq-11',
    question: 'Quantas escolas estão previstas?',
    answer: 'A estruturação prevê a implantação de até 20 Unidades de Educação Infantil (UEIs), que poderão operar em dois turnos ou em período integral.',
    category: 'escopo'
  },
  {
    id: 'faq-12',
    question: 'Quem está estruturando o projeto?',
    answer: 'A Prefeitura de Porto Velho é a responsável pelo projeto, na condição de Poder Concedente. A CAIXA Econômica Federal atua como agente estruturador, com recursos do Fundo de Estruturação de Projetos (FEP).',
    category: 'governanca'
  }
]

// LINK DO GOOGLE FORMS
const GOOGLE_FORMS_URL = 'https://forms.gle/bs3wFWDdBTZFe5RZ9'

// URLs reais das notícias
const newsItems = [
  {
    id: 'news-1',
    title: 'Educação de qualidade: Prefeitura sela projeto para ampliar vagas na educação infantil',
    date: '2026-02-11',
    summary: 'A Prefeitura de Porto Velho deu um passo importante para a ampliação das vagas na educação infantil com a formalização do projeto de Parceria Público-Privada (PPP) para a construção e gestão de novas unidades.',
    imageUrl: 'https://d2xsxph8kpxj0f.cloudfront.net/310419663030643880/XwhBSw6dLjE9gRphMVpAsy/participacao-social-k5r23XP4duMatHs8tX6pML.webp',
    link: 'https://agencia.portovelho.ro.gov.br/artigo/49185/educacao-de-qualidade-prefeitura-sela-projeto-para-ampliar-vagas-na-educacao-infantil'
  },
  {
    id: 'news-2',
    title: 'Infância: Educação Infantil é planejado via grupo de trabalho integrado',
    date: '2026-04-01',
    summary: 'A educação infantil em Porto Velho vem sendo planejada por meio de um grupo de trabalho integrado, que reúne diversas secretarias municipais para garantir qualidade no atendimento às crianças.',
    imageUrl: 'https://d2xsxph8kpxj0f.cloudfront.net/310419663030643880/XwhBSw6dLjE9gRphMVpAsy/escola-moderna-9jKH6cuH4RiS6z6k5k4LCS.webp',
    link: 'https://agencia.portovelho.ro.gov.br/artigo/53827/infancia-educacao-infantil-e-planejado-via-grupo-de-trabalho-integrado'
  }
]

function useScrollAnimation(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.unobserve(element)
        }
      },
      { threshold }
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [threshold])

  return { ref, isVisible }
}

function HeroSection() {
  const { ref, isVisible } = useScrollAnimation(0.1)
  const [videoOpen, setVideoOpen] = useState(false)

  return (
    <section id="hero" className="relative min-h-[85vh] flex items-center overflow-hidden" aria-label="Seção principal">
      <div className="absolute inset-0">
        <img
          src="https://d2xsxph8kpxj0f.cloudfront.net/310419663030643880/XwhBSw6dLjE9gRphMVpAsy/efmm-hero-banner-krxP5GPbUXXFLuyJHYbUYD.webp"
          alt="Vista panorâmica da Estrada de Ferro Madeira-Mamoré com o Rio Madeira ao fundo em Porto Velho"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#175FA3]/90 via-[#1D71C3]/80 to-[#1D71C3]/60" />
      </div>

      <div ref={ref} className="container relative z-10 py-20 md:py-28">
        <div className={`max-w-2xl transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
            <Landmark size={14} className="text-[#FACC1F]" />
            <span className="text-white/90 text-xs font-medium">Prefeitura Municipal de Porto Velho</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
            Educa Porto Velho
            <span className="block text-[#FACC1F] text-xl sm:text-2xl md:text-3xl mt-1 font-semibold">PPP Educação Infantil</span>
          </h1>
          <p className="text-white/90 text-base sm:text-lg leading-relaxed mb-8 max-w-xl">
            Parceria para ampliar vagas e melhorar a infraestrutura da Educação Infantil em Porto Velho.
            Acompanhe o projeto e participe da construção do futuro da educação na nossa cidade.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href={GOOGLE_FORMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#51A64D] text-white font-semibold rounded-lg hover:bg-[#458f3f] transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            >
              <MessageSquare size={18} />
              Consulta Pública
            </a>
            <a
              href="#documentos"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#51A64D] text-white font-semibold rounded-lg hover:bg-[#458f3f] transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
            >
              Documentos
              <ArrowRight size={16} />
            </a>
          </div>
        </div>

        <div className={`absolute right-4 bottom-8 md:right-8 md:bottom-12 lg:right-12 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <button
            onClick={() => setVideoOpen(true)}
            className="group flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/20 hover:bg-white/20 transition-all"
            aria-label="Assistir vídeo institucional"
          >
            <div className="w-10 h-10 bg-[#FACC1F] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
              <Play size={18} className="text-[#175FA3] ml-0.5" fill="#175FA3" />
            </div>
            <div className="hidden md:block text-left">
              <div className="text-white text-xs font-semibold">Vídeo Institucional</div>
              <div className="text-white/60 text-[10px]">Conheça o projeto</div>
            </div>
          </button>
        </div>
      </div>

      {videoOpen && (
        <div
          className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setVideoOpen(false)}
          role="dialog"
          aria-label="Vídeo institucional"
        >
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif font-bold text-lg">Vídeo Institucional</h3>
              <button onClick={() => setVideoOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">
                ×
              </button>
            </div>
            <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/TKF3oKRXojY?si=_qgOfqBhjBnpF7CA"
                title="Vídeo Institucional"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
            <p className="text-xs text-gray-400 mt-3">
              <a href="#" className="underline">Ver transcrição/legenda</a>
            </p>
          </div>
        </div>
      )}
    </section>
  )
}

function AboutSection() {
  const { ref, isVisible } = useScrollAnimation()

  return (
    <section id="sobre" className="py-16 md:py-20 bg-white" aria-labelledby="sobre-titulo">
      <div ref={ref} className="container">
        <div className={`transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-block text-[#1D71C3] text-xs font-semibold uppercase tracking-wider mb-2">Contexto</span>
            <h2 id="sobre-titulo" className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              O que é PPP e por que é importante?
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div>
              <p className="text-gray-700 leading-relaxed mb-4">
                PPP é uma parceria contratual de longo prazo entre o poder público e uma empresa privada para entregar infraestrutura e serviços de apoio com metas de desempenho e fiscalização.
                Esses arranjos são especialmente relevantes para projetos de infraestrutura em setores como saúde, educação e iluminação pública.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                No caso da Educação Infantil, a PPP apoia a construção e a manutenção do ambiente escolar e serviços não pedagógicos, enquanto a Prefeitura mantém a gestão pedagógica e as decisões educacionais.
                É uma forma eficiente de ampliar a oferta de vagas com qualidade e previsibilidade orçamentária.
              </p>
              <div className="bg-blue-50/80 rounded-lg p-4 mb-4">
                <h3 className="font-serif font-bold text-sm text-gray-900 mb-2">Em poucas palavras:</h3>
                <ul className="space-y-1.5 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><CircleCheck size={14} className="text-[#51A64D] mt-0.5 shrink-0" /> Contrato de longo prazo com metas claras de desempenho</li>
                  <li className="flex items-start gap-2"><CircleCheck size={14} className="text-[#51A64D] mt-0.5 shrink-0" /> Investimento privado em infraestrutura escolar</li>
                  <li className="flex items-start gap-2"><CircleCheck size={14} className="text-[#51A64D] mt-0.5 shrink-0" /> Vagas continuam públicas e gratuitas</li>
                  <li className="flex items-start gap-2"><CircleCheck size={14} className="text-[#51A64D] mt-0.5 shrink-0" /> Fiscalização contínua pelo poder público</li>
                  <li className="flex items-start gap-2"><CircleCheck size={14} className="text-[#51A64D] mt-0.5 shrink-0" /> Bens retornam ao Município ao final do contrato</li>
                </ul>
              </div>
            </div>
            <div className="space-y-6">
              <div className="bg-white border-2 border-[#1D71C3]/20 rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#1D71C3]/10 rounded-lg flex items-center justify-center">
                    <ShieldCheck size={20} className="text-[#1D71C3]" />
                  </div>
                  <h3 className="font-serif font-bold text-lg text-gray-900">PPP NÃO é privatização</h3>
                </div>
                <div className="space-y-3 text-sm text-gray-700">
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                    <CircleCheck size={16} className="text-[#51A64D] mt-0.5 shrink-0" />
                    <p><strong>Contrato de longo prazo</strong> com metas e indicadores de desempenho, não transferência definitiva de patrimônio.</p>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                    <CircleCheck size={16} className="text-[#51A64D] mt-0.5 shrink-0" />
                    <p><strong>Bens e serviços continuam públicos.</strong> As escolas construídas retornam ao Município ao final do contrato.</p>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                    <CircleCheck size={16} className="text-[#51A64D] mt-0.5 shrink-0" />
                    <p><strong>A parte pedagógica permanece com o Município.</strong> Professores, currículo e diretrizes educacionais são responsabilidade da Prefeitura.</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    Base legal: Lei Federal n.º 11.079/2004 (PPPs) e Lei Federal n.º 8.987/1995 (Concessões).
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function ProjectSection() {
  const { ref, isVisible } = useScrollAnimation()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  
  const projectCards = [
    { icon: <Target size={22} className="text-[#1D71C3]" />, title: 'Objetivo', desc: 'Ampliar o acesso à Educação Infantil e qualificar a infraestrutura escolar no município de Porto Velho.', bg: 'bg-blue-50' },
    { icon: <GraduationCap size={22} className="text-[#51A64D]" />, title: 'Escopo', desc: 'Construção e operação de até 20 Unidades de Educação Infantil (UEIs), com serviços de apoio não pedagógicos.', bg: 'bg-green-50' },
    { icon: <Wrench size={22} className="text-[#FACC1F]" />, title: 'O que muda', desc: 'Mais previsibilidade na manutenção, qualidade de infraestrutura e eficiência nos serviços de apoio escolar.', bg: 'bg-amber-50' },
    { icon: <GraduationCap size={22} className="text-[#175FA3]" />, title: 'O que NÃO muda', desc: 'Pedagogia, professores e diretrizes educacionais seguem integralmente sob responsabilidade da Prefeitura.', bg: 'bg-indigo-50' },
  ]

  const stats = [
    { number: '20', label: 'Unidades de Educação Infantil', color: 'text-[#1D71C3]' },
    { number: '24-27', label: 'Meses de estruturação', color: 'text-[#51A64D]' },
    { number: '4', label: 'Macroetapas do projeto', color: 'text-[#FACC1F]' },
    { number: '100%', label: 'Vagas públicas e gratuitas', color: 'text-[#175FA3]' }
  ]

  const projectImages = [
    { src: fachadaPerspectiva, alt: 'Fachada em perspectiva da unidade de educação infantil' },
    { src: fachadaFrontal, alt: 'Fachada frontal da unidade de educação infantil' },
    { src: refeitorio, alt: 'Refeitório planejado para as unidades de educação infantil' },
    { src: recreacao, alt: 'Área de recreação e lazer das unidades' },
    { src: berçario, alt: 'Berçário planejado com conforto e segurança' },
    { src: fraldario, alt: 'Fraldário com infraestrutura adequada' },
    { src: planoCortado, alt: 'Plano cortado da edificação - visão técnica' },
    { src: salaAtividades1, alt: 'Sala de atividades - espaço para aprendizado' },
    { src: salaAtividades2, alt: 'Sala de atividades - ambiente acolhedor' },
  ]

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % projectImages.length)
  }

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + projectImages.length) % projectImages.length)
  }

  useEffect(() => {
    const interval = setInterval(() => {
      nextImage()
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  return (
    <section id="projeto" className="py-16 md:py-20 bg-[#F5F7FA]" aria-labelledby="projeto-titulo">
      <div ref={ref} className="container">
        <div className={`transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-block text-[#51A64D] text-xs font-semibold uppercase tracking-wider mb-2">O Projeto</span>
            <h2 id="projeto-titulo" className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Educa Porto Velho – PPP Educação Infantil
            </h2>
            <p className="text-gray-600">Conheça os principais aspectos do projeto de Parceria Público-Privada para a Educação Infantil.</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-12">
            {projectCards.map((card, index) => (
              <div
                key={card.title}
                className={`${card.bg} rounded-xl p-6 border border-gray-100 hover:shadow-md transition-all duration-300`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 bg-white rounded-lg flex items-center justify-center shadow-sm shrink-0">
                    {card.icon}
                  </div>
                  <div>
                    <h3 className="font-serif font-bold text-base text-gray-900 mb-1">{card.title}</h3>
                    <p className="text-sm text-gray-700 leading-relaxed">{card.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {stats.map((stat) => (
              <div key={stat.label} className="bg-white rounded-xl p-5 border border-gray-100 text-center shadow-sm hover:shadow-md transition-shadow">
                <div className={`font-serif font-bold text-2xl md:text-3xl ${stat.color} mb-1`}>{stat.number}</div>
                <div className="text-xs text-gray-600 leading-snug">{stat.label}</div>
              </div>
            ))}
          </div>
          
          <div className="max-w-4xl mx-auto mb-12">
            <div className="relative rounded-xl overflow-hidden shadow-lg bg-gray-100">
              <div className="aspect-[16/9] md:aspect-[16/9]">
                <img
                  src={projectImages[currentImageIndex].src}
                  alt={projectImages[currentImageIndex].alt}
                  className="w-full h-full object-cover transition-opacity duration-500"
                  loading="lazy"
                />
              </div>
              
              <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-10">
                {projectImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`transition-all duration-300 rounded-full ${
                      currentImageIndex === index
                        ? 'w-8 h-2 bg-[#1D71C3]'
                        : 'w-2 h-2 bg-white/60 hover:bg-white/90'
                    }`}
                    aria-label={`Ir para imagem ${index + 1}`}
                  />
                ))}
              </div>
              
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 transition-all duration-300 backdrop-blur-sm z-10"
                aria-label="Imagem anterior"
              >
                <ChevronLeft size={24} />
              </button>
              
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 transition-all duration-300 backdrop-blur-sm z-10"
                aria-label="Próxima imagem"
              >
                <ChevronRight size={24} />
              </button>
              
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <p className="text-white text-sm text-center">
                  {currentImageIndex + 1} / {projectImages.length} - {projectImages[currentImageIndex].alt}
                </p>
              </div>
            </div>
            
            <div className="flex justify-center gap-2 mt-4 overflow-x-auto pb-2">
              {projectImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-16 h-12 rounded-md overflow-hidden transition-all duration-300 flex-shrink-0 ${
                    currentImageIndex === index
                      ? 'ring-2 ring-[#1D71C3] ring-offset-2'
                      : 'opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={image.src}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 md:p-8 border border-gray-100 shadow-sm">
            <h3 className="font-serif font-bold text-xl text-gray-900 mb-4 text-center">ESTRUTURADORES</h3>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                O município de Porto Velho foi selecionado para integrar o programa federal Fundo de Estruturação de Projetos (FEP – CAIXA), que apoia municípios na criação de PPPs para construir e gerir unidades de educação infantil, atualmente em fase de implantação em diversos municípios brasileiros. Essa seleção reforça a relevância do projeto, sua aderência às políticas públicas nacionais e o compromisso com soluções estruturadas e de longo prazo.
              </p>
              <p>
                A participação em uma iniciativa de abrangência nacional confere maior segurança, solidez institucional e consistência técnica às ações desenvolvidas no município.
              </p>
              <p>
                O projeto é estruturado pela CAIXA e conta com as diretrizes da Secretaria Especial do Programa de Parcerias de Investimentos (SEPPI), vinculada à Casa Civil do Governo Federal, e com financiamento do Fundo de Apoio à Estruturação de Projetos de Concessão e PPP (FEP). Essas instituições contribuem com apoio técnico, metodológico e institucional, garantindo que as ações desenvolvidas estejam alinhadas às diretrizes das políticas públicas nacionais, bem como às boas práticas de gestão, inovação e desenvolvimento social do município.
              </p>
            </div>
          </div>
          
        </div>
      </div>
    </section>
  )
}

function TimelineSection() {
  const { ref, isVisible } = useScrollAnimation()
  const [stages, setStages] = useState<any[]>([
    { id: '1', stage: 1, title: 'Planejamento, Diagnóstico e Estudos', description: 'Levantamento de dados, estudos de viabilidade, diagnósticos e estruturação inicial do projeto. Inclui a Escuta Social e a coleta de contribuições da comunidade.', status: 'em-andamento', duration: '8 meses' },
    { id: '2', stage: 2, title: 'EVTEA e Estruturação do Contrato', description: 'Elaboração do Estudo de Viabilidade Técnica, Econômica e Ambiental (EVTEA), modelagem econômico-financeira e minutas contratuais.', status: 'a-iniciar', duration: '5 a 6 meses' },
    { id: '3', stage: 3, title: 'Validação Externa', description: 'Consulta Pública, Audiência Pública, roadshows com investidores e revisões dos órgãos de controle.', status: 'a-iniciar', duration: '5 a 6 meses' },
    { id: '4', stage: 4, title: 'Licitação e Contratação', description: 'Publicação do edital, processo licitatório, seleção do parceiro privado e assinatura do contrato de concessão.', status: 'a-iniciar', duration: '6 a 7 meses' }
  ])
  const [isLoading, setIsLoading] = useState(true)

  const loadStages = async () => {
    try {
      const data = await api.getAllStages()
      if (data && data.length > 0) {
        setStages(data)
      }
    } catch (error) {
      console.error('Erro ao carregar fases:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadStages()
    
    const handleStagesUpdate = () => {
      loadStages()
    }
    window.addEventListener('stages-updated', handleStagesUpdate)
    return () => window.removeEventListener('stages-updated', handleStagesUpdate)
  }, [])

  const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
    'concluido': { bg: 'bg-[#51A64D]', text: 'text-white', label: 'Concluído' },
    'em-andamento': { bg: 'bg-[#FACC1F]', text: 'text-gray-900', label: 'Em andamento' },
    'a-iniciar': { bg: 'bg-gray-200', text: 'text-gray-600', label: 'A iniciar' }
  }

  return (
    <section id="timeline" className="py-16 md:py-20 bg-white" aria-labelledby="timeline-titulo">
      <div ref={ref} className="container">
        <div className={`transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-block text-[#1D71C3] text-xs font-semibold uppercase tracking-wider mb-2">Cronograma</span>
            <h2 id="timeline-titulo" className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Fases do Projeto
            </h2>
            <p className="text-gray-600 text-sm">
              Acompanhe o andamento de cada fase do projeto
            </p>
            {isLoading && (
              <p className="text-xs text-gray-400 mt-1">Atualizando...</p>
            )}
          </div>
          <div className="relative max-w-3xl mx-auto">
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" aria-hidden="true" />
            <div className="space-y-8">
              {stages.map((step) => {
                const config = statusConfig[step.status] || statusConfig['a-iniciar']
                const isEmAndamento = step.status === 'em-andamento'
                const isConcluido = step.status === 'concluido'
                return (
                  <div key={step.id} className="relative flex gap-5">
                    <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center shrink-0 border-4 border-white shadow-md ${
                      isEmAndamento ? 'bg-[#1D71C3] ring-4 ring-[#1D71C3]/20' :
                      isConcluido ? 'bg-[#51A64D]' : 'bg-gray-300'
                    }`}>
                      <span className="text-white font-bold text-sm">{step.stage}</span>
                    </div>
                    <div className="flex-1 pb-2">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-serif font-bold text-base text-gray-900">{step.title}</h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${config.bg} ${config.text}`}>
                          {isEmAndamento && <span className="w-1.5 h-1.5 bg-current rounded-full mr-1 animate-pulse" />}
                          {config.label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed mb-1">{step.description}</p>
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <Clock size={11} />
                        <span>Duração estimada: {step.duration}</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="flex justify-center mt-8">
            <button
              onClick={loadStages}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-[#1D71C3] hover:text-[#175FA3] transition-colors"
            >
              <RotateCcw size={14} />
              Atualizar fases
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

// src/pages/Home.tsx - Atualize a função EscutaSocialSection

function EscutaSocialSection() {
  const { ref, isVisible } = useScrollAnimation()
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isDownloading, setIsDownloading] = useState(false)
  const [report, setReport] = useState<Report | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const carouselImages = [
    { src: escuta1, alt: 'Oficina de Escuta Social - Participação comunitária em Porto Velho' },
    { src: escuta2, alt: 'Reunião da Escuta Social com cidadãos de Porto Velho' },
    { src: escuta3, alt: 'Registro da Escuta Ativa - Diálogo aberto com a comunidade' }
  ]

  // Buscar informações do relatório
  useEffect(() => {
    const loadReportInfo = async () => {
      try {
        const data = await api.getReport()
        setReport(data)
      } catch (error) {
        console.error('Erro ao buscar relatório:', error)
      } finally {
        setIsLoading(false)
      }
    }
    loadReportInfo()
  }, [])

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length)
  }

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + carouselImages.length) % carouselImages.length)
  }

  useEffect(() => {
    const interval = setInterval(() => {
      nextImage()
    }, 5000)
    return () => clearInterval(interval)
  }, [])

  const handleDownloadRelatorio = async () => {
    if (!report) return
    setIsDownloading(true)
    try {
      await api.downloadReport(report.id, report.originalName)
      toast.success('Download do relatório iniciado!')
    } catch (error: any) {
      console.error('Erro ao baixar relatório:', error)
      toast.error(error.message || 'Erro ao baixar relatório')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <section id="escuta-social" className="py-16 md:py-20 bg-[#F5F7FA]" aria-labelledby="escuta-titulo">
      <div ref={ref} className="container">
        <div className={`transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="inline-block text-[#51A64D] text-xs font-semibold uppercase tracking-wider mb-2">Participação Social</span>
            <h2 id="escuta-titulo" className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Escuta Social
            </h2>
            <p className="text-gray-600 leading-relaxed">
              A escuta social consiste em compreender as percepções, necessidades e experiências da comunidade, 
              a partir do diálogo aberto, da observação e da coleta de contribuições dos diversos públicos envolvidos.
            </p>
          </div>
          
          {/* CARROSSEL DE IMAGENS */}
          <div className="max-w-3xl mx-auto mb-8">
            <div className="relative rounded-xl overflow-hidden shadow-lg bg-gray-100">
              <div className="aspect-[16/9] md:aspect-[16/8]">
                <img
                  src={carouselImages[currentImageIndex].src}
                  alt={carouselImages[currentImageIndex].alt}
                  className="w-full h-full object-cover transition-opacity duration-500"
                  loading="lazy"
                />
              </div>
              
              <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-10">
                {carouselImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`transition-all duration-300 rounded-full ${
                      currentImageIndex === index
                        ? 'w-8 h-2 bg-[#1D71C3]'
                        : 'w-2 h-2 bg-white/60 hover:bg-white/90'
                    }`}
                    aria-label={`Ir para imagem ${index + 1}`}
                  />
                ))}
              </div>
              
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 transition-all duration-300 backdrop-blur-sm z-10"
                aria-label="Imagem anterior"
              >
                <ChevronLeft size={20} />
              </button>
              
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full p-2 transition-all duration-300 backdrop-blur-sm z-10"
                aria-label="Próxima imagem"
              >
                <ChevronRight size={20} />
              </button>
              
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <p className="text-white text-xs text-center">
                  {carouselImages[currentImageIndex].alt}
                </p>
              </div>
            </div>
            
            <div className="flex justify-center gap-2 mt-3">
              {carouselImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-16 h-12 rounded-md overflow-hidden transition-all duration-300 ${
                    currentImageIndex === index
                      ? 'ring-2 ring-[#1D71C3] ring-offset-2'
                      : 'opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={image.src}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* RELATÓRIO DA ESCUTA SOCIAL (DEVOLUTIVA) */}
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl p-6 border-2 border-[#1D71C3]/20 shadow-sm text-center">
              <FileText size={32} className="text-[#1D71C3] mx-auto mb-3" />
              <h3 className="font-serif font-bold text-lg text-gray-900 mb-2">
                {report?.title || 'Relatório da Escuta Social'}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                {report
                  ? `Acesse o relatório completo com a sistematização das contribuições recebidas durante o processo de Escuta Social. Esta é a devolutiva à comunidade.`
                  : 'O relatório da Escuta Social estará disponível em breve. Acompanhe esta página para atualizações.'}
              </p>

              {isLoading ? (
                <div className="flex justify-center py-4">
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-[#1D71C3] rounded-full animate-spin" />
                </div>
              ) : report ? (
                <button
                  onClick={handleDownloadRelatorio}
                  disabled={isDownloading}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#1D71C3] text-white font-semibold rounded-lg hover:bg-[#175FA3] transition-all disabled:opacity-60"
                >
                  {isDownloading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Baixando...
                    </>
                  ) : (
                    <>
                      <Download size={18} />
                      Baixar Relatório (PDF)
                    </>
                  )}
                </button>
              ) : (
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-500 rounded-lg cursor-not-allowed">
                  <Clock size={18} />
                  Em breve
                </div>
              )}

              {report?.updatedAt && (
                <p className="text-xs text-gray-400 mt-3">
                  Última atualização: {new Date(report.updatedAt).toLocaleDateString('pt-BR')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function FAQSection() {
  const { ref, isVisible } = useScrollAnimation()

  return (
    <section id="faq" className="py-16 md:py-20 bg-[#F5F7FA]" aria-labelledby="faq-titulo">
      <div ref={ref} className="container">
        <div className={`transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="inline-block text-[#51A64D] text-xs font-semibold uppercase tracking-wider mb-2">Dúvidas</span>
            <h2 id="faq-titulo" className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Perguntas Frequentes
            </h2>
            <p className="text-gray-600">
              Encontre respostas para as principais dúvidas sobre o projeto de PPP de Educação Infantil.
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="multiple" className="space-y-3">
              {faqItems.map((item) => (
                <AccordionItem key={item.id} value={item.id} className="bg-white rounded-lg border border-gray-100 shadow-sm px-5 overflow-hidden">
                  <AccordionTrigger className="text-left text-sm font-semibold text-gray-900 hover:text-[#1D71C3] py-4 [&[data-state=open]]:text-[#1D71C3]">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-gray-600 leading-relaxed pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  )
}

function NewsSection() {
  const { ref, isVisible } = useScrollAnimation()
  const formatDate = (dateStr: string) => new Date(dateStr + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })

  const handleNewsClick = (link: string) => {
    if (link && link !== '#') {
      window.open(link, '_blank', 'noopener noreferrer')
    } else {
      toast.info('Link da notícia será disponibilizado em breve.')
    }
  }

  return (
    <section id="noticias" className="py-16 md:py-20 bg-white" aria-labelledby="noticias-titulo">
      <div ref={ref} className="container">
        <div className={`transition-all duration-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="inline-block text-[#1D71C3] text-xs font-semibold uppercase tracking-wider mb-2">Comunicação</span>
            <h2 id="noticias-titulo" className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Releases e Notícias
            </h2>
            <p className="text-gray-600">
              Acompanhe as últimas novidades sobre o projeto de PPP de Educação Infantil.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {newsItems.map((item) => (
              <article key={item.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all group cursor-pointer" onClick={() => handleNewsClick(item.link)}>
                {item.imageUrl && (
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={item.imageUrl}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                )}
                <div className="p-5">
                  <time className="text-xs text-[#1D71C3] font-medium">{formatDate(item.date)}</time>
                  <h3 className="font-serif font-bold text-base text-gray-900 mt-1 mb-2 leading-snug">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-3">{item.summary}</p>
                  <div className="text-sm font-semibold text-[#1D71C3] hover:text-[#175FA3] inline-flex items-center gap-1 transition-colors">
                    Ler mais <ArrowRight size={14} />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

// Main Home Component
export default function Home() {
  return (
    <main id="conteudo-principal">
      <HeroSection />
      <WaveDivider position="bottom" fillColor="#FFFFFF" />
      <AboutSection />
      <WaveDivider position="bottom" fillColor="#F5F7FA" />
      <ProjectSection />
      <WaveDivider position="bottom" fillColor="#FFFFFF" />
      <TimelineSection />
      <WaveDivider position="bottom" fillColor="#F5F7FA" />
      <DocumentsSection />
      <WaveDivider position="bottom" fillColor="#FFFFFF" />
      <EscutaSocialSection />
      <WaveDivider position="bottom" fillColor="#F5F7FA" />
      <FAQSection />
      <WaveDivider position="bottom" fillColor="#FFFFFF" />
      <NewsSection />
    </main>
  )
}